/** @jsx jsx */
import React, { useMemo } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	CardAction,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
	type CardProps,
} from '@atlaskit/smart-card';
import { CardSSR as Card, type CardSSRProps } from '@atlaskit/smart-card/ssr';
import type { SmartLinkResponse } from '@atlaskit/linking-types';
import { useIntl } from '@atlassian/jira-intl';
import { generatePlaceholderData } from '../generate-placeholder-data.tsx';
import restrictedMessages from '../../restricted/messages.tsx';

const styles = cssMap({
	inlineStyles: {
		marginBlock: token('space.025'),
		backgroundColor: token('elevation.surface.raised'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		display: 'inline-flex',
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
		paddingInlineStart: token('space.025'),
	},
	titleBlockStyles: {
		gap: token('space.050'),
		height: '20px',
		maxHeight: '20px',
		minHeight: '20px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-smart-element-link]': {
			wordBreak: 'break-all',
			paddingInlineEnd: token('space.025'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& .actions-button-group': {
			backgroundColor: token('color.background.neutral'),
			'&:hover': {
				backgroundColor: token('color.background.neutral.hovered'),
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'[data-testid="smart-action"]': {
				'&:focus-visible': {
					outlineOffset: token('space.negative.050'),
				},
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-smart-element-lozenge]': {
			paddingBottom: token('space.025'),
			paddingRight: token('space.025'),
		},
	},
});

const uiOptions: CardProps['ui'] = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
	theme: SmartLinkTheme.Black,
};

const uiOptionsWithPlaceholder: CardProps['ui'] = {
	...uiOptions,
	removeBlockRestriction: true,
};

const actionOptions = {
	hide: false,
	exclude: [CardAction.ChangeStatusAction],
};

type TitleBlockProps = Parameters<typeof TitleBlock>[0];
export type Props = Pick<
	CardSSRProps,
	'url' | 'showHoverPreview' | 'onError' | 'onResolve' | 'hoverPreviewOptions'
> &
	Pick<
		TitleBlockProps,
		| 'anchorRef'
		| 'anchorTarget'
		| 'text'
		| 'metadata'
		| 'actions'
		| 'position'
		| 'icon'
		| 'hideRetry'
	> & {
		/** When defined this will display a remove button after the link that calls `onRemove` when clicked. */
		onRemove?: () => void;
	};

/**
 * This is a copy of SimpleInlineSmartLink, need some more work to make it work for atlas project
 * for now, we are using this component to avoid breaking changes
 * update SimpleInlineSmartLink with the changes made here later if possible
 *
 * See ticket: https://jplat.atlassian.net/browse/ANIP-217
 */
export const InlineSmartLink = ({
	url,
	anchorTarget,
	showHoverPreview = true,
	text,
	anchorRef,
	onError,
	onResolve,
	metadata = [{ name: ElementName.State }],
	position = SmartLinkPosition.Center,
	actions,
	icon,
	hideRetry,
	hoverPreviewOptions,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();

	const placeholderData = useMemo<SmartLinkResponse | undefined>(() => {
		if (!url) {
			return undefined;
		}

		return generatePlaceholderData(url, formatMessage(restrictedMessages.restrictedProject), text);
	}, [formatMessage, text, url]);

	return (
		<Box xcss={styles.inlineStyles}>
			<Card
				appearance="inline"
				url={url}
				ui={placeholderData ? uiOptionsWithPlaceholder : uiOptions}
				actionOptions={actionOptions}
				showHoverPreview={showHoverPreview}
				onError={onError}
				onResolve={onResolve}
				placeholderData={placeholderData}
				hoverPreviewOptions={hoverPreviewOptions}
			>
				<TitleBlock
					hideTitleTooltip
					maxLines={1}
					metadata={metadata}
					anchorTarget={anchorTarget ?? '_self'}
					text={text}
					icon={icon}
					css={styles.titleBlockStyles}
					anchorRef={anchorRef}
					position={position}
					actions={actions}
					hideRetry={hideRetry}
					hideIconLoadingSkeleton={!!placeholderData}
				/>
			</Card>
		</Box>
	);
};
